import React, { useState } from 'react';
import { Box, Button, IconButton, Stack, Typography, alpha } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import HealingIcon from '@mui/icons-material/Healing';
import { red } from '@mui/material/colors';
import { BuffOrDebuff, WarbandMemberDTO } from '../../types';
import { StatColumn } from './StatColumn';
import { getSplitStatValue } from '../../utils/splitStats';
import { useStatPopover } from '../../hooks/useStatPopover';
import { StatBreakdownPopover } from './StatBreakdownPopover';
import { CurrentHealthUpdateControls } from './CurrentHealthUpdateControls';
import { ApplyLevelUpModal } from '../modals/ApplyLevelUpModal';

interface MemberStatsAndHealthProps {
  member: WarbandMemberDTO;
  editMode: boolean;
  levelUpBonuses?: { fight: number; shoot: number; will: number; health: number };
  unusedLevelUps?: number;
  onApplyLevelUp?: (stat: string) => Promise<void>;
  onUpdateHealth: (health: number) => void;
  warbandBuffs: BuffOrDebuff[];
}

export const MemberStatsAndHealth: React.FC<MemberStatsAndHealthProps> = ({
  member,
  editMode,
  levelUpBonuses,
  unusedLevelUps,
  onApplyLevelUp,
  onUpdateHealth,
  warbandBuffs,
}) => {
  const [applyLevelUpOpen, setApplyLevelUpOpen] = useState(false);
  const { statPopover, openStatPopover, closeStatPopover } = useStatPopover();
  const maxHealth = getSplitStatValue(member, 'health', member.health + (levelUpBonuses?.health ?? 0), warbandBuffs);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: 0.5,
            flex: 1,
          }}
        >
          <StatColumn label="Move" value={member.move} adjustedValue={getSplitStatValue(member, 'move', member.move, warbandBuffs)} onClick={(e) => openStatPopover(e, member, 'move', 'Move', member.move, warbandBuffs)} />
          <StatColumn label="Fight" value={member.fight + (levelUpBonuses?.fight ?? 0)} adjustedValue={getSplitStatValue(member, 'fight', member.fight + (levelUpBonuses?.fight ?? 0), warbandBuffs)} onClick={(e) => openStatPopover(e, member, 'fight', 'Fight', member.fight + (levelUpBonuses?.fight ?? 0), warbandBuffs)} />
          <StatColumn label="Shoot" value={member.shoot + (levelUpBonuses?.shoot ?? 0)} adjustedValue={getSplitStatValue(member, 'shoot', member.shoot + (levelUpBonuses?.shoot ?? 0), warbandBuffs)} onClick={(e) => openStatPopover(e, member, 'shoot', 'Shoot', member.shoot + (levelUpBonuses?.shoot ?? 0), warbandBuffs)} />
          <StatColumn label="Armour" value={member.armour} adjustedValue={getSplitStatValue(member, 'armour', member.armour, warbandBuffs)} onClick={(e) => openStatPopover(e, member, 'armour', 'Armour', member.armour, warbandBuffs)} />
          <StatColumn label="Will" value={member.will + (levelUpBonuses?.will ?? 0)} adjustedValue={getSplitStatValue(member, 'will', member.will + (levelUpBonuses?.will ?? 0), warbandBuffs)} onClick={(e) => openStatPopover(e, member, 'will', 'Will', member.will + (levelUpBonuses?.will ?? 0), warbandBuffs)} />
          <StatColumn label="Health" value={member.health + (levelUpBonuses?.health ?? 0)} adjustedValue={getSplitStatValue(member, 'health', member.health + (levelUpBonuses?.health ?? 0), warbandBuffs)} onClick={(e) => openStatPopover(e, member, 'health', 'Health', member.health + (levelUpBonuses?.health ?? 0), warbandBuffs)} />
        </Box>
        {editMode && (unusedLevelUps ?? 0) > 0 && (
          <IconButton
            size="small"
            onClick={() => setApplyLevelUpOpen(true)}
            aria-label={`Apply level up (${unusedLevelUps} available)`}
            title={`${unusedLevelUps} level up${unusedLevelUps !== 1 ? 's' : ''} available`}
            sx={{
              backgroundColor: alpha('#FFD700', 0.55),
              ':hover': {
                backgroundColor: alpha('#FFD700', 0.75),
              },
            }}
          >
            <FileUploadIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ mt: 0, pt: 0 }}>
        {editMode ? (
          <Stack spacing={1} direction="row">
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
            <CurrentHealthUpdateControls
              member={member}
              saving={false}
              healthBonus={levelUpBonuses?.health ?? 0}
              onSave={(nextHealth) => onUpdateHealth(nextHealth)}
            />
            </Box>
            <Button
              sx={{ position: 'relative' }}
              variant="contained"
              size="medium"
              startIcon={<HealingIcon />}
              onClick={() => onUpdateHealth(maxHealth)}
              disabled={member.currentHealth >= maxHealth}
            >
              Heal to full
            </Button>
          </Stack>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
            <CurrentHealthUpdateControls
              member={member}
              saving={false}
              healthBonus={levelUpBonuses?.health ?? 0}
              onSave={(nextHealth) => onUpdateHealth(nextHealth)}
            />
          </Box>
        )}
      </Box>
      <StatBreakdownPopover popover={statPopover} onClose={closeStatPopover} />
      {onApplyLevelUp && (
        <ApplyLevelUpModal
          open={applyLevelUpOpen}
          onClose={() => setApplyLevelUpOpen(false)}
          onApply={onApplyLevelUp}
        />
      )}
    </>
  );
};
